// src/lib/pages.ts — product page content (PAGES-02). Same rules as ops.ts: every read and write goes through Postgres
// functions that check the caller's role. Drafts: pricing_admin and owner. Publish: owner only. Prices never live here —
// a price in page copy is refused by the database (page_doc_check).
import { supabase } from "./supabase";
import { OpsError } from "./ops";

export type PageDoc = { blocks: unknown[]; [k: string]: unknown };

export interface PageRow {
  product_key: string;
  name: string;
  product_status: string;
  path: string | null;
  slug: string | null;
  generated: boolean;
  page_status: "draft" | "live" | null;
  draft_version: number | null;
  live_version: number | null;
  unpublished_changes: boolean;
  draft_updated_at: string | null;
  draft_updated_by: string | null;
  published_at: string | null;
  published_by: string | null;
}

export interface PageLogEntry { at: string; action: string; actor: string; reason: string | null }

export interface PageDetail {
  product_key: string;
  name: string;
  path: string | null;
  status: "draft" | "live";
  draft_version: number;
  live_version: number | null;
  draft: PageDoc;
  live: PageDoc | null;
  draft_updated_at: string;
  draft_updated_by: string | null;
  published_at: string | null;
  published_by: string | null;
  log: PageLogEntry[];
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args ?? {});
  if (error) throw new OpsError({ message: pageMessage(error.message), code: error.code });
  return data as T;
}

/** The database's page errors, in plain words. */
function pageMessage(m: string): string {
  if (m.startsWith("page_copy_has_price: ")) return "Prices can't be typed into page copy — the server fills them in. " + m.slice(21);
  if (m.startsWith("publish_only_by_owner")) return "Only the owner can publish.";
  if (m.startsWith("image_not_licensed: ")) return "An image has no approved license yet: " + m.slice(20);
  if (m.startsWith("no_page_path")) return "This product has no page address yet (products.page_paths).";
  if (m === "reason_required") return "Say briefly why (at least 5 characters).";
  return m;
}

export const pages = {
  list: () => rpc<PageRow[]>("ops_pages_list"),
  get: (key: string) => rpc<PageDetail | null>("ops_page_get", { p_product_key: key }),
  saveDraft: (key: string, draft: PageDoc, reason: string) =>
    rpc<{ product_key: string; draft_version: number; status: string }>("page_save_draft", { p: { product_key: key, draft, reason } }),
  publish: (key: string, reason: string) =>
    rpc<{ product_key: string; status: string; live_version: number }>("page_publish", { p_product_key: key, p_reason: reason }),
  /** Starts the site deploy so a published page goes online (Edge Function pages-deploy). Resolves false if it isn't set up. */
  deploy: async (): Promise<boolean> => {
    const { data, error } = await supabase.functions.invoke("pages-deploy", { body: {} });
    if (error) return false;
    return !!(data as { ok?: boolean } | null)?.ok;
  },
};

// ── Editable text ─────────────────────────────────────────────────────────
// The editor shows every piece of customer-facing wording and nothing else. Structure (block types, links, images,
// price references, module settings) stays with Claude sessions and code review.
const TECH_KEYS = new Set([
  "type", "id", "tone", "variant", "layout", "art", "asset", "href", "request", "style", "product", "return_path", "bg", "s", "per",
  "service_id", "schema", "source_prefix", "lead_label", "species", "spots", "order", "art_label_prefix", "max_width", "start", "module",
]);
const BLOCK_NAME: Record<string, string> = {
  "campaign-banner": "QR banner", hero: "Top of page", cards: "Cards", figure: "Illustration", gallery: "Photos", "price-table": "Size & price table",
  quote: "Price widget", options: "Ways to start", checklist: "Standards", included: "Included / not included", faq: "Questions", final: "Closing",
};

export interface TextField { path: (string | number)[]; label: string; value: string; long: boolean }

function words(k: string | number): string {
  return typeof k === "number" ? `#${k + 1}` : k.replace(/_/g, " ");
}

/** Every editable string in the document, with a readable label. */
export function textFields(doc: PageDoc): TextField[] {
  const out: TextField[] = [];
  const walk = (v: unknown, path: (string | number)[], label: string[]) => {
    if (typeof v === "string") {
      if (/^#[0-9A-Fa-f]{6}$/.test(v)) return;
      out.push({ path, label: label.join(" › "), value: v, long: v.length > 70 });
      return;
    }
    if (Array.isArray(v)) { v.forEach((x, i) => walk(x, [...path, i], [...label, words(i)])); return; }
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      for (const [k, x] of Object.entries(o)) {
        if (TECH_KEYS.has(k)) continue;
        if (k === "price") { const p = x as Record<string, unknown>; if (typeof p?.suffix === "string") walk(p.suffix, [...path, k, "suffix"], [...label, "price note"]); continue; }
        if (k === "canvas-plants") continue;
        walk(x, [...path, k], [...label, words(k)]);
      }
    }
  };
  for (const [k, v] of Object.entries(doc)) {
    if (k === "blocks") {
      (v as Record<string, unknown>[]).forEach((b, i) => walk(b, ["blocks", i], [`${i + 1}. ${BLOCK_NAME[String(b.type)] ?? String(b.type)}`]));
    } else if (!TECH_KEYS.has(k)) walk(v, [k], [words(k)]);
  }
  return out;
}

export function getAt(doc: unknown, path: (string | number)[]): unknown {
  return path.reduce<unknown>((o, k) => (o == null ? undefined : (o as Record<string | number, unknown>)[k]), doc);
}

/** A copy of the document with one string replaced. */
export function setAt(doc: PageDoc, path: (string | number)[], value: string): PageDoc {
  const copy = JSON.parse(JSON.stringify(doc)) as PageDoc;
  let o = copy as unknown as Record<string | number, unknown>;
  for (const k of path.slice(0, -1)) o = o[k] as Record<string | number, unknown>;
  o[path[path.length - 1]] = value;
  return copy;
}
